import { DateTime, Interval } from 'luxon';

// Within our app, there are 3 different date formats. All of them are variations of the iso format:
// - GQL uses the full iso format
// - Inputs of type "date" use the "iso day" format
// - Inputs of type "datetime-local" use the full iso format without timezone

type IsoDateString = string;
type Date = DateTime | IsoDateString;

const _timezone = 'Europe/Berlin';
const _locale = 'de';

// Format functions
export function formatDate(date?: Date, format: string = 'dd.MM.yy', placeholder: string = '-'): string {
    if (!date) return placeholder;

    const realDate = typeof date === 'string' ? toDateTime(date) : date;
    _validateTimeZone(realDate);
    return realDate.toFormat(format, { locale: _locale });
}

export function formatDateRange(from: Date, to: Date, placeholder: string = '-', separator: string = ' - '): string {
    if (!from && !to) return placeholder;
    if (!to) return `${formatDate(from)} - ∞`;
    if (!from) return `∞ - ${formatDate(from)}`;
    return `${formatDate(from)}${separator}${formatDate(to)}`;
}

// Conversion between date types
export function toDateTime(date: Date): DateTime {
    const isDateTime = typeof date !== 'string';
    if (isDateTime) {
        _validateTimeZone(date);
        return date;
    }

    return DateTime.fromISO(date, { zone: _timezone });
}

function _validateTimeZone(date: DateTime) {
    // We convert all dates to german timezone during parse/creation. If a date is not in german timezone during output, this is a bug.
    if (import.meta.env.DEV && date.zoneName !== _timezone) {
        console.warn(`Tried to format a non-german date: ${date}`);
    }
}

export function insideRangeCheck(objStart: DateTime, objEnd: DateTime, rangeStart: DateTime, rangeEnd: DateTime) {
    const rangeInterval = Interval.fromDateTimes(rangeStart, rangeEnd);
    const objectInterval = Interval.fromDateTimes(objStart, objEnd);
    return objectInterval.overlaps(rangeInterval);
}
